/*
 * Tables, Chairs & Tents builder - /pages/tables-chairs-builder
 *
 * Independent quantity selectors for the existing table, chair and tent
 * products. Add to Event sends ONE /cart/add.js request containing a separate
 * line for each item whose quantity is above zero.
 *
 * Deliberately self contained:
 *   - it does not touch product-form.js, product-info.js or cart.js
 *   - it defines its own custom element and modifies no existing one
 *   - variant ids and prices are read from the markup, so pricing always comes
 *     from Shopify and is never restated in JavaScript
 *
 * The cart icon is refreshed through Shopify's Section Rendering API - the
 * same mechanism the rest of the theme uses after an AJAX cart change - and
 * the theme's cartUpdate event is published so anything else listening updates
 * itself. No existing cart behaviour is changed.
 *
 * 2026-08-04 (rev B): deep linking. Arriving from a Chairs or Tables link
 * scrolls that row into view, highlights it briefly and puts the cursor in its
 * quantity box, so it is obvious where to start. See applyDeepLink().
 *
 * ---------------------------------------------------------------------------
 * 2026-08-29 (Phase 1): tents, dependent rows and a live summary. Backup of
 * the previous revision is assets/jj-backup-20260829-jj-tables-chairs-builder.js.
 *
 * DEPENDENT ROWS
 * A row carrying data-tcb-requires="<anchor>" is only usable while the row with
 * that data-tcb-anchor has a quantity of one or more. syncDependencies() runs
 * before every total recalculation and:
 *
 *   - reveals or hides the row
 *   - enables or disables its input AND its stepper buttons
 *   - forces the quantity to zero the moment the controller returns to zero
 *   - caps the quantity at the controller's quantity, so one 10x30 tent can
 *     never carry two sets of screen walls
 *
 * selection() skips disabled inputs, so a hidden dependent row can never
 * contribute a cart line. That is what makes an orphan Screen Walls line
 * impossible rather than merely unlikely.
 *
 * The dependency is read from the markup. Adding another dependent row needs
 * no change to this file.
 * ---------------------------------------------------------------------------
 */

(function () {
  'use strict';

  var MAX_PER_ITEM = 99;
  var HIGHLIGHT_MS = 2600;

  /*
   * Hash aliases. The homepage Individual Pricing links use #tables-6ft,
   * #tables-8ft and #chairs, but people share and hand-type these, so the
   * obvious variations resolve too rather than silently doing nothing.
   */
  var ANCHOR_ALIASES = {
    chairs: 'chairs',
    chair: 'chairs',
    'white-chairs': 'chairs',
    'white-folding-chair': 'chairs',
    tables: '6ft',
    table: '6ft',
    '6ft': '6ft',
    '6-ft': '6ft',
    'tables-6ft': '6ft',
    '6-ft-tables': '6ft',
    '6-ft-table': '6ft',
    '8ft': '8ft',
    '8-ft': '8ft',
    'tables-8ft': '8ft',
    '8-ft-tables': '8ft',
    '8-ft-table': '8ft',
    tent: 'tent-10x20',
    tents: 'tent-10x20',
    '10x20': 'tent-10x20',
    '10x20-tent': 'tent-10x20',
    'tent-10x20': 'tent-10x20',
    '10x20-canopy-tent': 'tent-10x20',
    '10x30': 'tent-10x30',
    '10x30-tent': 'tent-10x30',
    'tent-10x30': 'tent-10x30',
    '10x30-canopy-tent': 'tent-10x30',
    'screen-walls': 'screen-walls',
    'screenwalls': 'screen-walls',
    'tent-10x30-screen-walls': 'screen-walls',
    '10x30-screen-walls-add-on': 'screen-walls',
  };

  function toInt(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function clamp(value, ceiling) {
    var top = typeof ceiling === 'number' ? ceiling : MAX_PER_ITEM;
    if (value < 0) return 0;
    if (value > top) return top;
    return value;
  }

  if (customElements.get('jj-tables-chairs-builder')) return;

  customElements.define(
    'jj-tables-chairs-builder',
    class JJTablesChairsBuilder extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-tcb-input]'));

        if (!this.inputs.length) {
          console.error('Tables and Chairs builder: no quantity inputs found.');
          return;
        }

        this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-tcb-row]'));
        this.dependentRows = this.rows.filter(function (row) {
          return !!row.dataset.tcbRequires;
        });
        this.submitButton = this.querySelector('[data-tcb-submit]');
        this.errorBox = this.querySelector('[data-tcb-error]');
        this.successBox = this.querySelector('[data-tcb-success]');
        this.totalOutput = this.querySelector('[data-tcb-total]');
        this.summaryList = this.querySelector('[data-tcb-summary-list]');
        this.summaryEmpty = this.querySelector('[data-tcb-summary-empty]');
        this.currency = this.dataset.currency || 'USD';
        this.pending = false;
        this.highlightTimer = null;

        try {
          this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currency,
          });
        } catch (error) {
          this.formatter = null;
        }

        this.bindInputs();
        this.bindSteppers();

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        }

        this.refreshTotals();

        this.boundDeepLink = this.applyDeepLink.bind(this);
        window.addEventListener('hashchange', this.boundDeepLink);

        // Deferred one frame so the browser has finished its own hash jump and
        // the layout has settled before we scroll.
        window.requestAnimationFrame(this.boundDeepLink);
      }

      disconnectedCallback() {
        if (this.boundDeepLink) window.removeEventListener('hashchange', this.boundDeepLink);
        if (this.highlightTimer) window.clearTimeout(this.highlightTimer);
      }

      /* --------------------------------------------------------- deep links */

      rowForAnchor(anchor) {
        var match = null;

        this.rows.forEach(function (row) {
          if (row.dataset.tcbAnchor === anchor) match = row;
        });

        return match;
      }

      applyDeepLink() {
        var raw = (window.location.hash || '').replace('#', '').toLowerCase().trim();
        if (!raw) return;

        var anchor = ANCHOR_ALIASES[raw];
        if (!anchor) return;

        var row = this.rowForAnchor(anchor);
        if (!row) return;

        /*
         * A dependent row that is not currently available cannot be used, so
         * the link lands on the row that unlocks it instead of scrolling to
         * something invisible.
         */
        if (row.hidden && row.dataset.tcbRequires) {
          var controller = this.rowForAnchor(row.dataset.tcbRequires);
          if (!controller) return;
          row = controller;
        }

        this.highlight(row);

        try {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (error) {
          row.scrollIntoView();
        }

        // Focus the quantity box so the customer can simply start typing. Not
        // selected, so a keyboard opening on mobile does not hide the row.
        var input = row.querySelector('[data-tcb-input]');
        if (input && !input.disabled) {
          window.setTimeout(function () {
            try {
              input.focus({ preventScroll: true });
            } catch (error) {
              input.focus();
            }
          }, 400);
        }
      }

      highlight(row) {
        var self = this;

        this.rows.forEach(function (candidate) {
          candidate.classList.remove('jj-tcb__row--highlight');
        });

        if (this.highlightTimer) window.clearTimeout(this.highlightTimer);

        row.classList.add('jj-tcb__row--highlight');

        this.highlightTimer = window.setTimeout(function () {
          row.classList.remove('jj-tcb__row--highlight');
          self.highlightTimer = null;
        }, HIGHLIGHT_MS);
      }

      /* ------------------------------------------------------ dependencies */

      /*
       * Quantity of one row, read live from its input. Returns zero for a row
       * that does not exist or whose input is disabled.
       */
      quantityForAnchor(anchor) {
        var row = this.rowForAnchor(anchor);
        if (!row) return 0;

        var input = row.querySelector('[data-tcb-input]');
        if (!input || input.disabled) return 0;

        return clamp(toInt(input.value));
      }

      /*
       * Reveals, caps or resets every dependent row. Called at the top of
       * refreshTotals(), so it can never be skipped on a path that then
       * prices or submits the selection.
       */
      syncDependencies() {
        var self = this;

        this.dependentRows.forEach(function (row) {
          var input = row.querySelector('[data-tcb-input]');
          if (!input) return;

          var ceiling = self.quantityForAnchor(row.dataset.tcbRequires);
          var active = ceiling > 0;

          row.hidden = !active;
          row.classList.toggle('jj-tcb__row--locked', !active);
          input.disabled = !active;

          if (!active) {
            // Reset rather than remember. A remembered quantity would silently
            // reappear - and be charged for - if the tent came back.
            input.value = '0';
            input.max = String(MAX_PER_ITEM);
          } else {
            input.max = String(ceiling);
            input.value = String(clamp(toInt(input.value), ceiling));
          }

          Array.prototype.slice.call(row.querySelectorAll('[data-tcb-step]')).forEach(function (button) {
            button.disabled = !active;
          });
        });
      }

      /* Highest quantity this input may hold right now. */
      ceilingFor(input) {
        var row = input.closest('[data-tcb-row]');
        if (row && row.dataset.tcbRequires) {
          return this.quantityForAnchor(row.dataset.tcbRequires);
        }
        return MAX_PER_ITEM;
      }

      /* ------------------------------------------------------------ totals */

      money(cents) {
        if (this.formatter) return this.formatter.format(cents / 100);
        return '$' + (cents / 100).toFixed(2);
      }

      bindInputs() {
        var self = this;

        this.inputs.forEach(function (input) {
          input.addEventListener('input', function () {
            self.refreshTotals();
          });

          // Normalise only on blur, so typing "12" is not rewritten to "1".
          input.addEventListener('blur', function () {
            input.value = String(clamp(toInt(input.value), self.ceilingFor(input)));
            self.refreshTotals();
          });
        });
      }

      bindSteppers() {
        var self = this;

        Array.prototype.slice.call(this.querySelectorAll('[data-tcb-step]')).forEach(function (button) {
          button.addEventListener('click', function () {
            if (button.disabled) return;

            var row = button.closest('[data-tcb-row]');
            var input = row ? row.querySelector('[data-tcb-input]') : null;
            if (!input || input.disabled) return;

            input.value = String(
              clamp(toInt(input.value) + toInt(button.dataset.tcbStep), self.ceilingFor(input))
            );
            self.refreshTotals();
          });
        });
      }

      /*
       * Current selection, ignoring anything set to zero and anything whose
       * input is disabled. This is the single source the summary, the total
       * and the cart payload are all built from, so they cannot disagree.
       */
      selection() {
        var items = [];

        this.inputs.forEach(function (input) {
          if (input.disabled) return;

          var quantity = clamp(toInt(input.value));
          if (quantity < 1) return;

          items.push({
            id: toInt(input.dataset.tcbVariant),
            quantity: quantity,
            unitPrice: toInt(input.dataset.tcbPrice),
            label: input.dataset.tcbLabel || '',
          });
        });

        return items;
      }

      refreshTotals() {
        var self = this;
        var total = 0;

        this.syncDependencies();

        this.inputs.forEach(function (input) {
          var row = input.closest('[data-tcb-row]');
          var quantity = input.disabled ? 0 : clamp(toInt(input.value));
          var lineTotal = quantity * toInt(input.dataset.tcbPrice);
          total += lineTotal;

          var lineOutput = row ? row.querySelector('[data-tcb-line]') : null;
          if (lineOutput) lineOutput.textContent = self.money(lineTotal);

          var lineWrapper = row ? row.querySelector('.jj-tcb__line') : null;
          if (lineWrapper) lineWrapper.classList.toggle('jj-tcb__line--zero', quantity < 1);

          var minus = row ? row.querySelector('[data-tcb-step="-1"]') : null;
          if (minus && !input.disabled) minus.disabled = quantity < 1;

          var plus = row ? row.querySelector('[data-tcb-step="1"]') : null;
          if (plus && !input.disabled) plus.disabled = quantity >= self.ceilingFor(input);
        });

        if (this.totalOutput) this.totalOutput.textContent = this.money(total);

        this.renderSummary();

        // Clearing a stale error the moment the customer fixes the problem.
        if (total > 0) this.hide(this.errorBox);

        return total;
      }

      /* ----------------------------------------------------------- summary */

      renderSummary() {
        if (!this.summaryList) return;

        var self = this;
        var items = this.selection();

        this.summaryList.innerHTML = '';

        items.forEach(function (item) {
          var entry = document.createElement('li');
          entry.className = 'jj-tcb__summary-item';

          var name = document.createElement('span');
          name.className = 'jj-tcb__summary-name';
          name.textContent = item.quantity + ' × ' + item.label;

          var price = document.createElement('span');
          price.className = 'jj-tcb__summary-price';
          price.textContent = self.money(item.quantity * item.unitPrice);

          entry.appendChild(name);
          entry.appendChild(price);
          self.summaryList.appendChild(entry);
        });

        if (this.summaryEmpty) this.summaryEmpty.hidden = items.length > 0;
      }

      /* ------------------------------------------------------------ submit */

      show(box, html) {
        if (!box) return;
        box.innerHTML = html;
        box.hidden = false;
      }

      hide(box) {
        if (!box) return;
        box.hidden = true;
        box.innerHTML = '';
      }

      setPending(isPending) {
        this.pending = isPending;
        var button = this.submitButton;
        if (!button) return;

        button.setAttribute('aria-disabled', isPending ? 'true' : 'false');
        button.classList.toggle('loading', isPending);

        var spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isPending);

        var label = button.querySelector('[data-tcb-submit-label]');
        if (label) label.textContent = isPending ? 'Adding...' : 'Add to Event';
      }

      onSubmit(event) {
        event.preventDefault();

        if (this.pending) return;

        this.hide(this.successBox);

        var items = this.selection();

        if (!items.length) {
          this.show(
            this.errorBox,
            'Choose at least one table, chair or tent before adding to your event.'
          );
          if (this.inputs[0]) this.inputs[0].focus();
          return;
        }

        this.hide(this.errorBox);
        this.addToEvent(items);
      }

      addToEvent(items) {
        var self = this;
        var root = (window.rentalRoutes && window.rentalRoutes.rootUrl) || '/';
        var addUrl = (window.routes && window.routes.cart_add_url) || root + 'cart/add.js';

        this.setPending(true);

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items.map(function (item) {
              return { id: item.id, quantity: item.quantity };
            }),
          }),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              var message =
                (result.data && (result.data.description || result.data.message)) ||
                'We could not add those items. Please try again.';
              self.show(self.errorBox, message);
              return null;
            }

            self.onAdded(items);
            return self.refreshCartIcon(root);
          })
          .catch(function (error) {
            console.error('Tables and Chairs builder: add to event failed', error);
            self.show(
              self.errorBox,
              'We could not reach the cart just now. Please check your connection and try again.'
            );
          })
          .then(function () {
            self.setPending(false);
          });
      }

      onAdded(items) {
        var eventUrl =
          (window.rentalRoutes && window.rentalRoutes.eventDetailsUrl) || '/pages/event-details';

        var summary = items
          .map(function (item) {
            return item.quantity + ' x ' + item.label;
          })
          .join(', ');

        this.show(
          this.successBox,
          'Added to your event: ' +
            summary +
            '. <a href="' +
            eventUrl +
            '">Review your event details</a>'
        );

        // Reset the selectors so a second visit to the page starts clean and a
        // double click cannot silently add the same items twice. Resetting the
        // tent to zero also re-locks and clears the screen walls row, via
        // syncDependencies() inside refreshTotals().
        this.inputs.forEach(function (input) {
          input.value = '0';
        });
        this.refreshTotals();
      }

      /*
       * Section Rendering API. The response wraps the section in
       * <div id="shopify-section-cart-icon-bubble">, so the swap source is
       * .shopify-section - #cart-icon-bubble is the <a> in this theme's header
       * and does not appear in the response at all.
       */
      refreshCartIcon(root) {
        return fetch(root + '?sections=cart-icon-bubble')
          .then(function (response) {
            return response ? response.json() : null;
          })
          .then(function (sections) {
            if (!sections || !sections['cart-icon-bubble']) return;

            var parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            var source = parsed.querySelector('.shopify-section');
            var destination = document.querySelector('#cart-icon-bubble');

            if (source && destination) destination.innerHTML = source.innerHTML;

            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jj-tables-chairs-builder' });
              }
            } catch (error) {
              // The icon has already been re-rendered; the event is a bonus.
            }
          })
          .catch(function (error) {
            console.error('Tables and Chairs builder: cart icon refresh failed', error);
          });
      }
    }
  );
})();
